import { createAction, createListenerMiddleware } from '@reduxjs/toolkit';
import { locationChanged } from '../ducks/routing.duck';

// Señales de analítica. No tienen reducer ni tocan el store: existen solo para
// que el middleware de abajo las escuche y avise a los handlers registrados.
// Así hay UN único camino hacia GA4 (componente → dispatch → middleware →
// handlers), en vez de que los componentes hablen con gtag por su cuenta.
export const valuationProgressed = createAction('analytics/valuationProgressed');
export const signedUp = createAction('analytics/signedUp');
export const leadGenerated = createAction('analytics/leadGenerated');
export const listingPublished = createAction('analytics/listingPublished');

// La interfaz de los handlers es opcional: LoggingAnalyticsHandler y
// GoogleAnalyticsHandler no tienen por qué implementar todos los métodos, y
// alguien puede enchufar el suyo. Se comprueba antes de llamar.
const notifyHandlers = (handlers, method, ...args) => {
  handlers.forEach(handler => {
    if (typeof handler[method] === 'function') {
      handler[method](...args);
    }
  });
};

// Create a Redux Toolkit listener middleware for analytics handlers. Each
// handler can implement the following methods:
//
// - trackPageView(canonicalPath, previousPath): called when the URL is changed
// - trackSignUp({ userType, interests }): called after a successful signup
// - trackGenerateLead(): called after a successful contact form submission
// - trackListingPublished({ listingType }): called after a draft becomes published
export const createAnalyticsListenerMiddleware = handlers => {
  const listenerMiddleware = createListenerMiddleware();

  listenerMiddleware.startListening({
    actionCreator: locationChanged,
    effect: (action, listenerApi) => {
      const { canonicalPath } = action.payload;
      const previousPath = listenerApi.getOriginalState()?.routing?.currentCanonicalPath;

      handlers.forEach(handler => {
        handler.trackPageView(canonicalPath, previousPath);
      });
    },
  });

  listenerMiddleware.startListening({
    actionCreator: valuationProgressed,
    effect: action => notifyHandlers(handlers, 'trackValuation', action.payload),
  });

  listenerMiddleware.startListening({
    actionCreator: signedUp,
    effect: action => {
      notifyHandlers(handlers, 'trackSignUp', action.payload);
    },
  });

  listenerMiddleware.startListening({
    actionCreator: leadGenerated,
    effect: () => {
      notifyHandlers(handlers, 'trackGenerateLead');
    },
  });

  listenerMiddleware.startListening({
    actionCreator: listingPublished,
    effect: action => {
      notifyHandlers(handlers, 'trackListingPublished', action.payload);
    },
  });

  return listenerMiddleware;
};

// Legacy export for backward compatibility
export const createMiddleware = createAnalyticsListenerMiddleware;
